import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Copy, Check, Loader } from "lucide-react";
import api, { getErrorMessage } from "../lib/api";
import Button from "./common/Button";

interface MFASetupProps {
  required?: boolean;           // Hvis true: blokker cancel, tving fullføring
  onComplete: () => void;       // Kalles når MFA er bekreftet
  onCancel?: () => void;        // Valgfri avbryt-callback (kun hvis !required)
}

export default function MFASetup({ required, onComplete, onCancel }: MFASetupProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<"init" | "setup" | "verify" | "backup" | "done">("init");
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const startSetup = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.post("/auth/mfa/setup");
      setSecret(res.data.secret);
      setUri(res.data.provisioning_uri);
      setStep("setup");
    } catch (e: any) {
      setError(getErrorMessage(e, t("mfa.setup_failed")));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (!code.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await api.post("/auth/mfa/verify", { code: code.trim() });
      setBackupCodes(res.data.backup_codes || []);
      setStep("backup");
    } catch (e: any) {
      setError(getErrorMessage(e, t("mfa.invalid_code")));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (step === "init") startSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generer QR-kode URL via Google Charts (eller en inline SVG om du vil unngå ekstern)
  const qrUrl = uri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(uri)}`
    : "";

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const copyAllBackups = () => copy(backupCodes.join("\n"), "all");

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-[10px] bg-night/[0.06] flex items-center justify-center">
            <Shield className="w-5 h-5 text-night" />
          </div>
          <div>
            <h1 className="text-[22px] font-extrabold text-night leading-none">{t("mfa.title")}</h1>
            <p className="text-[13px] text-muted mt-1">
              {required ? t("mfa.required_for_role") : t("mfa.protect_account")}
            </p>
          </div>
        </div>

        {loading && step === "init" && (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-6 h-6 animate-spin text-faint" />
          </div>
        )}

        {step === "setup" && (
          <div className="space-y-5">
            <div className="rounded-[14px] p-5 bg-black/[0.03]">
              <p className="text-[13px] text-muted mb-3">{t("mfa.scan_qr")}</p>
              {qrUrl && (
                <div className="flex justify-center mb-3">
                  <img src={qrUrl} alt="MFA QR" className="rounded-[10px]" />
                </div>
              )}
              <p className="text-[12px] text-muted mb-1.5">{t("mfa.manual_secret")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-[13px] bg-white rounded-[8px] px-3 py-2 tabular break-all">{secret}</code>
                <button onClick={() => copy(secret, "secret")}
                  className="p-2 rounded-[8px] hover:bg-black/[0.05] transition-colors" title={t("common.kopier")}>
                  {copied === "secret" ? <Check className="w-4 h-4 text-pos" /> : <Copy className="w-4 h-4 text-muted" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("mfa.enter_code_from_app")}</label>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456" autoFocus inputMode="numeric" pattern="[0-9]*"
                onKeyDown={e => e.key === "Enter" && verify()}
                className="field-ios text-center text-[20px] tabular tracking-widest font-bold" />
            </div>

            {error && <div className="rounded-[10px] bg-neg/10 text-neg px-3 py-2.5 text-[13px]">{error}</div>}

            <div className="flex flex-col gap-2">
              <Button variant="primary" fullWidth onClick={verify} loading={loading} disabled={code.length !== 6}>
                {t("mfa.verify_activate")}
              </Button>
              {!required && onCancel && (
                <Button variant="neutral" fullWidth onClick={onCancel}>{t("common.avbryt")}</Button>
              )}
            </div>
          </div>
        )}

        {step === "backup" && (
          <div className="space-y-5">
            <div className="rounded-[14px] p-4 bg-pos/[0.08] text-[13px] text-pos font-semibold">
              ✓ {t("mfa.activated")}
            </div>
            <div className="rounded-[14px] p-5 bg-flag/[0.08]">
              <p className="text-[13px] font-bold text-night mb-2">{t("mfa.backup_title")}</p>
              <p className="text-[12.5px] text-muted mb-3">{t("mfa.backup_warning")}</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {backupCodes.map((c, i) => (
                  <code key={i} className="font-mono text-[13px] bg-white rounded-[6px] px-2 py-1.5 text-center tabular">
                    {c}
                  </code>
                ))}
              </div>
              <Button variant="secondary" fullWidth onClick={copyAllBackups}>
                {copied === "all" ? <><Check className="w-4 h-4" /> {t("mfa.copied")}</> : <><Copy className="w-4 h-4" /> {t("mfa.copy_all")}</>}
              </Button>
            </div>
            <Button variant="primary" fullWidth onClick={onComplete}>
              {t("mfa.done_saved")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
